import React, { useState } from 'react';
import { Content } from './style';
import { SectionName, Tab, TabsDiv } from '../../../components/styles/subheader-style';
import { Filter as FilterIcon } from 'lucide-react'
import { Filter, Header } from '../../../components/SubHeaderFilter/types'
import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import { productionFashionMovementHeaders } from '../ProductionFashion';
import PageHeader from '../../../components/StandardPage/PageHeader';

import { Outlet, useNavigate, useOutletContext, useMatch } from 'react-router-dom';
import { TypeMainOutlet } from '..';
import movement from '../../../assets/stock/TableMovementHeader/refresh-active.svg';
import timeFast from '../../../assets/production-flow/time-fast.svg';
import ModuleKanban from '../Pipelines/ModuleKanban';
import { useSearch } from '../../../contexts/SearchContext';

const productionOrderHeaders: Header[] = [
  { key: 'codeOfOP', label: 'OP', type: 'string' },
  { key: 'item', label: 'Item', type: 'string' },
  { key: 'quantity', label: 'Quantidade', type: 'number' },
  { key: 'materials', label: 'Materiais', type: 'string' },
  { key: 'resources', label: 'Insumos', type: 'string' },
  { key: 'services', label: 'Serviços', type: 'string' },
  { key: 'order', label: 'Pedido', type: 'number' },
  { key: 'weight', label: 'Peso', type: 'number' },
  { key: 'status', label: 'Status', type: 'string', selectOptions: [
    { value: 'Em aberto', label: 'Em aberto' },
    { value: 'Em produção', label: 'Em produção' },
    { value: 'Concluído', label: 'Concluído' },
  ]},
  { key: 'currentStage', label: 'Etapa atual', type: 'string' },
  { key: 'client', label: 'Cliente', type: 'string' },
]

export interface ProductionOutletType {
  selectedStage: number;
  setSelectedStage: React.Dispatch<React.SetStateAction<number>>;
  tabIndex: number;
  setTabIndex: React.Dispatch<React.SetStateAction<number>>;
  search: string;
  quantity: number;
  setQuantity: React.Dispatch<React.SetStateAction<number>>;
  refresh: boolean;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  appliedFilters: Filter[]
}

import { usePermissions } from '../../../hooks/usePermissions'

const TAB_INDEX_PRODUCTION = 0;
const TAB_INDEX_MOVEMENT = 1;
const TAB_INDEX_BOARD = 2;

const tabs = [
  { path: '/panel/production', image: timeFast, name: 'Ordem de produção', permission: 'ordens_producao', index: TAB_INDEX_PRODUCTION },
  { path: '/panel/production/movement', image: movement, name: 'Movimentação', permission: 'movimentacao', index: TAB_INDEX_MOVEMENT },
  { path: '/panel/production/board', image: movement, name: 'Funil', permission: 'funil_producao', index: TAB_INDEX_BOARD },
];

const ProductionFlow = () => {
  const { clearSearch } = useSearch();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const movementMatch = useMatch('/panel/production/movement');
  const boardMatch = useMatch('/panel/production/board');
  const createMatch = useMatch('/panel/production/create');
  const editMatch = useMatch('/panel/production/edit/:id');

  const tabIndex = boardMatch ? TAB_INDEX_BOARD : (movementMatch ? TAB_INDEX_MOVEMENT : TAB_INDEX_PRODUCTION);
  const isCreateOrEditPage = createMatch || editMatch;

  const [selectedStage, setSelectedStage] = useState<number>(1);
  const { search } = useOutletContext<TypeMainOutlet>();
  const [quantity, setQuantity] = useState(50);
  const [refresh, setRefresh] = useState(false);

  const orderFiltersState = usePersistedFilters('production_order')
  const movementFiltersState = usePersistedFilters('production_movement')

  const activeFiltersState = tabIndex === TAB_INDEX_MOVEMENT ? movementFiltersState : orderFiltersState

  const visibleTabs = tabs.filter((t) => hasPermission(t.permission))

  return (
    <>
      {!isCreateOrEditPage && !boardMatch && (
        <PageHeader
          title="Produção"
          tableName="ProductionFlow"
          moduleName="Produção"
          tabs={visibleTabs.map((tab) => ({ key: String(tab.index), label: tab.name, icon: tab.image }))}
          activeTab={String(tabIndex)}
          onTabChange={(key) => {
            const target = tabs.find((tab) => String(tab.index) === key)
            if (target) navigate(target.path)
          }}
          onReload={() => setRefresh((previous) => !previous)}
          filterHeaders={tabIndex === TAB_INDEX_MOVEMENT ? productionFashionMovementHeaders : productionOrderHeaders}
          appliedFilters={activeFiltersState.appliedFilters}
          onApplyFilter={activeFiltersState.handleApplyFilter}
          onRemoveFilter={activeFiltersState.handleRemoveFilter}
          onClearAllFilters={activeFiltersState.handleClearAll}
          createButton={tabIndex === TAB_INDEX_MOVEMENT ? undefined : { label: 'Ordem de produção', onClick: () => navigate('/panel/production/create') }}
        />
      )}

      {tabIndex === TAB_INDEX_BOARD && !isCreateOrEditPage ? (
        <ModuleKanban
          moduleType="production"
          moduleName="Produção"
          subModule="steel"
          headerLeftContent={
            <>
              <SectionName>Produção</SectionName>
              <TabsDiv>
                {visibleTabs.map((tab) => (
                  <Tab
                    key={tab.index}
                    $isFirst={tab.index === 0}
                    $isLast={tab.index === tabs.length - 1}
                    $isActive={tab.index === tabIndex}
                    onClick={() => {
                      navigate(tab.path)
                    }}
                  >
                    {tab.index === TAB_INDEX_BOARD ? (
                      <FilterIcon size={12.8} style={{ marginRight: 7 }} />
                    ) : (
                      <img src={tab.image} alt={`${tab.path}-icon`} />
                    )}
                    {tab.name}
                  </Tab>
                ))}
              </TabsDiv>
            </>
          }
        />
      ) : (
        <Content>
          <Outlet
            context={{
              selectedStage,
              setSelectedStage,
              tabIndex,
              setTabIndex: () => {},
              search,
              quantity,
              setQuantity,
              refresh,
              setRefresh,
              appliedFilters: activeFiltersState.appliedFilters,
            }}
          />
        </Content>
      )}
    </>
  );
};

export default ProductionFlow;
